import { Suspense, useContext, useState } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  ApplyStatusToSelectedAction,
  ArrangeReturnCollectionAction,
  BulkSaveSelectedAction
} from '../../../lib/context/SmartSendContextModels';
import { SmartSendSummary } from '../../../lib/helpers/SmartSendFilterApplier';
import { ApplierOption } from '../../../lib/helpers/ApplierOption';
import Tags from './Tags';
import ApplyDimensions from './ApplyDimensions';
import ApplyService from './ApplyService';
import { Dimensions, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import Extras from './Extras';
import { useEffect } from 'react';
import { ExportToCsv } from '../../csv/ExportToCsv';
import { GetSelectionBasedOnAction } from '../../../lib/helpers/GetSelectionBasedOnAction';
import {
  SmartSendCsvExportSoldHeaders,
  MapSmartSendSoldItemToCsv,
  SmartSendCsvExportBookedHeaders,
  SmartSendCsvExportOrderHeaders,
  MapOrderToCsv,
  MapSmartSendBookedItemToCsv
} from '../../../lib/helpers/CsvMapperHelper';
import Restore from './Restore';
import Delete from './Delete';
import SaveForLater from './SaveForLater';
import ApplyRecipientContactDetails from './ApplyContactDetails';
import { GetSelectedItems } from '../../../lib/helpers/GetSelectedItems';
import ConfirmReturnCollection from './ConfirmReturnCollection';
import { GetBulkActionTitle } from './GetBulkActionTitle';
import ApplyPreferredCollectionDate from './ApplyPreferredCollectionDate';
import DownloadLabels from './DownloadLabels';
import Archive from './Archive';
import SmartSendActionList from './SmartSendActionList';
import ConfirmDispatch from './ConfirmDispatch';
import { EventSections } from '../../../lib/events/EventLogger';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ProtectionExtraIds } from '../../ExtrasHelper';
import Protection from './Protection';
import { Tagged } from '../../Tagged';
import { PanelButton } from '../panels/PanelButtons';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { P2GSidePanel } from '~/components/sidepanel';
import { P2GBox } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ApplyTariffCodes from './ApplyTariffCodes';
import GenerateOptions from '~/apps/smartsend/lib/helpers/SmartSendBulkActionOptions';

interface props {
  open: boolean;
  action?: string;
  onClose: () => void;
  filter: SmartSendSummary;
  filterKey: string;
  exclude: ApplierOption[];
  smartSendItemsCache: SmartSendItemApiModel[];
  onActionClick: (a: ApplierOption, close: () => void) => void;
}
export default function SmartSendSoldItemsAddAction(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { createRule, getQuoteProviders, getRules, getServiceTags, getTags, updateRule, updateServiceTags } =
    useSmartSendApi();
  const ctx = useContext(SmartSendContext);
  const { getQuoteOptions, getPackages } = useSmartSendApi();
  const [action, setAction] = useState('LIST');

  useEffect(() => {
    setAction(props.action);
  }, [props.action]);

  const affected = (action: string) => {
    return props.filter.bulkOptions.filter((o) => o.includes(action)).length;
  };

  const onActionClick = (a: ApplierOption) => {
    props.onActionClick(a, close);
    setAction(a);
  };

  const reset = () => {
    setAction('LIST');
  };

  const close = () => {
    reset();
    props.onClose();
  };

  const RenderView = () => {
    switch (action) {
      case 'Status:Active':
        return (
          <Restore
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Active', false));
            }}
            cancel={reset}
          />
        );
      case 'Status:Deleted':
        return (
          <Delete
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Deleted', true));
            }}
            cancel={reset}
          />
        );
      case 'Status:Archived':
        return (
          <Archive
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Archived', true));
            }}
            cancel={reset}
          />
        );
      case 'Status:Inactive':
        return (
          <SaveForLater
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Inactive', true));
            }}
            cancel={reset}
          />
        );
      case 'Service:Add':
      case 'Service:Change':
        return (
          <ApplyService
            getServiceTags={() => getServiceTags().then((r) => (r.success ? r.result.Tags : []))}
            updateServiceTags={updateServiceTags}
            affected={affected(action)}
            selectedItems={() => GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache)}
            loggingSections={[EventSections.SmartSend, EventSections.SmartSendItem]}
            confirm={(service, serviceOption) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  [action, 'Service', 'ServiceOption'],
                  {
                    Service: service.Service,
                    ServiceOption: serviceOption
                  },
                  false
                )
              );
            }}
            quoteProviders={() =>
              getQuoteProviders().then((r) => {
                return r.success ? r.result.Providers : [];
              })
            }
            getQuotes={async (
              request,
              packageDimensions: Dimensions,
              quoteAmount: number,
              quoteSlug: string | null
            ) => {
              const r = await getQuoteOptions(
                request.provider,
                request.accountId,
                {
                  Recipient: request.recipient,
                  Sender: request.sender,
                  PackageDimensions: packageDimensions,
                  MarketplaceOrderLines: [{ Value: quoteAmount, Source: { Id: quoteSlug } }],
                  Value: quoteAmount
                } as SmartSendItemApiModel,
                null,
                request.includeTags,
                request.excludeTags
              );
              return r.success ? r.result.Quotes : [];
            }}
            cancel={close}
          />
        );

      case 'TariffCodes:Add':
        return (
          <ApplyTariffCodes
            affected={affected(action)}
            items={() =>
              GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache).filter((x) =>
                GenerateOptions(x, x.State.validation).includes(action)
              )
            }
            confirm={(codes) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  [action, 'TariffCodes'],
                  {
                    TariffCodes: codes
                  },
                  false
                )
              );
            }}
          />
        );

      case 'Dimensions:Change':
      case 'Dimensions:Add':
        return (
          <ApplyDimensions
            showDefaultDimensions={SettingsCache.settings.Features.includes('DefaultDimensionsRule')}
            showDefaultWeight={SettingsCache.settings.Features.includes('DefaultWeightRule')}
            preSelect={action === 'Dimensions:Add'}
            affected={affected(action)}
            confirm={(dims) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(props.filterKey, [action, 'Dimensions'], { Dimensions: dims }, false)
              );
            }}
            getPackages={() => {
              return getPackages().then((r) => {
                return r.success ? r.result.Packages : [];
              });
            }}
            getRules={(t) => {
              return getRules(t).then((r) => {
                return r.success ? r.result.Rules : [];
              });
            }}
            createRule={(r) => {
              return createRule(r).then((r) => r.success);
            }}
            updateRule={(r) => {
              return updateRule(r).then((r) => r.success);
            }}
            cancel={reset}
          />
        );
      case 'Download:Labels':
        return <DownloadLabels affected={affected(action)} cancel={reset} />;
      case 'PreferredCollectionDate':
        return (
          <ApplyPreferredCollectionDate
            affected={affected(action)}
            confirm={(force, date) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  ['PreferredCollectionDate'],
                  { PreferredCollectionDate: date },
                  force
                )
              );
            }}
            cancel={reset}
          />
        );
      case 'RecipientContactDetails:Add':
      case 'RecipientContactDetails:Change':
        return (
          <ApplyRecipientContactDetails
            affected={affected(action)}
            customer={ctx.state.customer || { Email: '', Phone: '' }}
            confirm={(customer) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  [action, 'RecipientContactDetails'],
                  { RecipientContactDetails: customer },
                  false
                )
              );
            }}
            cancel={reset}
          />
        );
      case 'Return':
        return (
          <ConfirmReturnCollection
            confirm={() => {
              const items = GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache);
              ctx.dispatch(new ArrangeReturnCollectionAction(items.map((x) => x.Id)));
              reset();
            }}
            cancel={reset}
            affected={affected(action)}
          />
        );
      case 'Tags':
        return (
          <Tags
            section={EventSections.BulkActionSideBar}
            confirm={(tags) => {
              reset();
              ctx.dispatch(new BulkSaveSelectedAction(props.filterKey, ['Tags'], { Tags: tags.sort() }, false));
            }}
            getTags={() => {
              return getTags().then((r) => {
                return r.success ? r.result.Tags : [];
              });
            }}
            cancel={reset}
          />
        );
      case 'Download:CSV:Sold':
        return (
          <ExportToCsv
            mode="manual"
            fileName="SmartSend_SoldItems_Export"
            headers={SmartSendCsvExportSoldHeaders}
            data={() => {
              const data = GetSelectionBasedOnAction(props.filterKey, ctx.state, action, props.smartSendItemsCache).map(
                MapSmartSendSoldItemToCsv
              );
              return {
                fields: SmartSendCsvExportSoldHeaders(),
                data: data
              };
            }}
          />
        );
      case 'Download:CSV:Booked':
        return (
          <ExportToCsv
            mode="manual"
            fileName="SmartSend_BookedItems_Export"
            headers={SmartSendCsvExportBookedHeaders}
            data={() => {
              const data = GetSelectionBasedOnAction(props.filterKey, ctx.state, action, props.smartSendItemsCache).map(
                MapSmartSendBookedItemToCsv
              );
              return {
                fields: SmartSendCsvExportBookedHeaders(),
                data: data
              };
            }}
          />
        );
      case 'Download:CSV:Orders':
        return (
          <ExportToCsv
            mode="manual"
            fileName="SmartSend_Orders_Export"
            headers={SmartSendCsvExportOrderHeaders}
            data={() => {
              const data = GetSelectionBasedOnAction(props.filterKey, ctx.state, action, props.smartSendItemsCache).map(
                MapOrderToCsv
              );
              return {
                fields: SmartSendCsvExportOrderHeaders(),
                data: data
              };
            }}
          />
        );
      case 'Dispatch:Confirm':
        return (
          <ConfirmDispatch
            filterKey={props.filterKey}
            cancel={reset}
            affected={affected('MarkAsDispatched')}
            confirm={() => onActionClick('Dispatch')}
          />
        );
      case 'Extras':
        return (
          <Extras
            include={[]}
            exclude={ProtectionExtraIds}
            showDefaultRule={SettingsCache.settings.Features.includes('DefaultExtrasRule')}
            affected={affected(action)}
            getSelectedItems={() => GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache)}
            confirm={(extras) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  ['Extras'],
                  {
                    Extras: extras.map((x) => {
                      return { ...x, Total: 0, Price: 0, Vat: 0 };
                    })
                  },
                  false
                )
              );
            }}
            cancel={reset}
            getRules={(t) => {
              return getRules(t).then((r) => {
                return r.success ? r.result.Rules : [];
              });
            }}
            createRule={(r) => {
              return createRule(r).then((r) => r.success);
            }}
            updateRule={(r) => {
              return updateRule(r).then((r) => r.success);
            }}
          />
        );

      case 'Protection':
        return (
          <Protection
            protectionIds={ProtectionExtraIds}
            showDefaultRule={SettingsCache.settings.Features.includes('DefaultExtrasRule')}
            confirm={(extras) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  ['Extras'],
                  {
                    Extras: extras.map((x) => {
                      return { ...x, Total: 0, Price: 0, Vat: 0 };
                    })
                  },
                  false
                )
              );
            }}
            cancel={reset}
            getRules={(t) => {
              return getRules(t).then((r) => {
                return r.success ? r.result.Rules : [];
              });
            }}
            createRule={(r) => {
              return createRule(r).then((r) => r.success);
            }}
            updateRule={(r) => {
              return updateRule(r).then((r) => r.success);
            }}
          />
        );

      default:
        return (
          <P2GBox mb={2}>
            <SmartSendActionList
              affected={affected}
              exclude={props.exclude}
              onActionClick={onActionClick}
              bulkOptions={props.filter.bulkOptions}
              onClose={close}
            />
          </P2GBox>
        );
    }
  };

  if (['Service:Add', 'Service:Change'].includes(action)) {
    return (
      <Suspense>
        <RenderView />
      </Suspense>
    );
  }

  return (
    <P2GSidePanel
      open={props.open}
      onClose={close}
      anchor={'right'}
      width="600px"
      title={GetBulkActionTitle(t, action)}
      actions={
        action === 'LIST' ? (
          <PanelButton
            text={t('CLOSE_ACTIONS_BUTTON', { defaultValue: 'Close Actions' })}
            colour="tertiary"
            onClick={close}
          />
        ) : null
      }
    >
      {props.open && (
        <Tagged tag="panel-actions">
          <Suspense>
            <RenderView />
          </Suspense>
        </Tagged>
      )}
    </P2GSidePanel>
  );
}
